import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { CompanyDraft, DealAssociation, LandholdingDraft, ProjectDraft, SelectData } from "../../../../models";
import { deleteProject } from "../../../../service/project";
// import { CompanyDraft, DealAssociation, LandholdingDraft, ProjectDraft, SelectData } from "../../../../models";
import { searchProjects, SearchProjectsRequest } from "../../../../service/publicquery";
import { searchCollaborators } from "../../../../service/query";
import { FilterGroup, FilterGroups, ServiceError, Status } from "../../../../service/Shared";
import { getDefaultInvestmentDirectoryProjectFilterGroups } from "../../../../utils/project/projectSearchFilters";
import { Toast } from "../../../../widget";
import { NewDealJourneyContext } from "../NewDealJourneyContext";

const LANDHOLDING_OPTION_PREFIX = "landholding:";
const getProjectSearchFilterGroups = (
  searchTerm: string
): FilterGroup<NonNullable<SearchProjectsRequest["filter"]>>[] => {
  const listingFilterGroups = getDefaultInvestmentDirectoryProjectFilterGroups();

  if (!searchTerm) {
    return listingFilterGroups;
  }

  return [
    {
      operator: "and",
      filterGroups: [
        ...listingFilterGroups,
        {
          operator: "or",
          filterGroups: [
            {
              operator: "and",
              filter: {
                results: {
                  displayName: {
                    operator: "startsWith",
                    value: searchTerm,
                  },
                },
              },
            },
            {
              operator: "and",
              filter: {
                results: {
                  landholdings: {
                    some: {
                      groupName: {
                        operator: "startsWith",
                        value: searchTerm,
                      },
                    },
                  },
                },
              } as never,
            },
          ],
        },
      ],
    },
  ];
};

interface UseDealAssociationsReturnData {
  errors: ServiceError[] | undefined;
  investingIntoSelectData: SelectData;
  companySelectData: SelectData;
  projectsSelectData: SelectData;
  selectedInvestedInto: string | null;
  associations: DealAssociation[];
  companyDraft: CompanyDraft | null;
  projectDrafts: ProjectDraft[];
  landholdingDrafts: LandholdingDraft[];
  showAddEditCompanyModal: boolean;
  showProjectCreatePanel: boolean;
  openAddEditCompanyModal: () => void;
  closeAddEditCompanyModal: () => void;
  openProjectCreatePanel: () => void;
  closeProjectCreatePanel: () => void;
  saveCompanyDraft: (draft: CompanyDraft) => void;
  saveProjects: (projects: ProjectDraft[], landholding: LandholdingDraft, isLandholding: boolean) => void;
  clearCompanyDraft: () => void;
  removeLandholdingDraft: (draftKey: string) => void;
  removeProjectDraft: (draftKey: string) => Promise<void>;
  onCompanySelect: (value: string) => void;
  onInvestedIntoSelect: (value: string) => void;
  onProjectSelect: (value: string) => void;
  onCompanyTypeSearch: (value: string) => void;
  onProjectTypeSearch: (value: string) => void;
  removeAssociation: (value: DealAssociation) => void;
}

const investingIntoSelectData = [
  { key: "Company", value: "Company" },
  { key: "Project(s)", value: "Project(s)" },
  { key: "Company + Project(s)", value: "Company + Project(s)" },
];

export const useDealAssociations = (): UseDealAssociationsReturnData => {
  const { errors, formValues, setFormValues, handleFieldChange } = useContext(NewDealJourneyContext);
  const [companySelectData, setCompanySelectData] = useState<SelectData>([]);
  const [projectsSelectData, setProjectsSelectData] = useState<SelectData>([]);
  const [showAddEditCompanyModal, setShowAddEditCompanyModal] = useState(false);
  const [showProjectCreatePanel, setShowProjectCreatePanel] = useState(false);

  const { associations, selectedInvestedInto, companyDraft, projectDrafts, landholdingDrafts } = formValues;

  const filteredProjectsSelectData = useMemo<SelectData>(() => {
    const selectedKeys = new Set(
      associations
        .filter((association) => association.objectType === "Project" || association.objectType === "Landholding")
        .map((association) =>
          association.objectType === "Landholding"
            ? `${LANDHOLDING_OPTION_PREFIX}${association.objectUuid}`
            : association.objectUuid
        )
    );

    return projectsSelectData.filter((option) => !selectedKeys.has(String(option.key)));
  }, [associations, projectsSelectData]);

  const fetchCompanies = useCallback(async (searchTerm: string) => {
    const response = await searchCollaborators({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 25,
      },
      sort: [{ key: "results.name", direction: "asc" }],
      filter: searchTerm
        ? {
            results: {
              name: {
                operator: "contains",
                value: searchTerm,
              },
            },
          }
        : undefined,
    });

    if (response.status === Status.Success && response.data?.results) {
      setCompanySelectData(response.data.results.map((company) => ({ key: company.uuid, value: company.name })));
      return;
    }

    setCompanySelectData([]);
  }, []);

  const fetchProjects = useCallback(async (searchTerm: string) => {
    const response = await searchProjects({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 50,
      },
      sort: [{ key: "results.displayName", direction: "asc" }],
      filterGroups: getProjectSearchFilterGroups(searchTerm),
    } as SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>);

    if (response.status === Status.Success && response.data?.results) {
      const options = new Map<string, SelectData[number]["value"]>();

      response.data.results.forEach((project) => {
        if (project.uuid) {
          options.set(project.uuid, `${project.displayName} | Project`);
        }

        project.landholdings?.forEach((landholding) => {
          if (!landholding.groupUuid) return;
          const name = `${landholding.groupName} | Landholding`;
          options.set(`${LANDHOLDING_OPTION_PREFIX}${landholding.groupUuid}`, name);
        });
      });

      setProjectsSelectData(
        Array.from(options.entries())
          .map(([key, value]) => ({ key, value }))
          .sort((left, right) => String(left.value ?? "").localeCompare(String(right.value ?? "")))
      );
      return;
    }

    setProjectsSelectData([]);
  }, []);

  useEffect(() => {
    fetchCompanies("");
    fetchProjects("");
  }, [fetchCompanies, fetchProjects]);

  const onCompanyTypeSearch = useCallback(
    (value: string) => {
      fetchCompanies(value);
    },
    [fetchCompanies]
  );

  const onProjectTypeSearch = useCallback(
    (value: string) => {
      fetchProjects(value);
    },
    [fetchProjects]
  );

  const onInvestedIntoSelect = useCallback(
    (value: string) => {
      handleFieldChange("selectedInvestedInto")(value || null);
    },
    [handleFieldChange]
  );

  const onCompanySelect = (uuid: string): void => {
    if (uuid === "") return;
    setFormValues((prev) => ({
      ...prev,
      associations: [
        ...prev.associations,
        {
          objectUuid: uuid,
          objectType: "Collaborator",
          name: companySelectData.find((x) => x.key === uuid)?.value as string,
        },
      ],
    }));
  };

  const onProjectSelect = (uuid: string): void => {
    if (uuid === "") return;

    const isLandholding = uuid.startsWith(LANDHOLDING_OPTION_PREFIX);
    const objectUuid = isLandholding ? uuid.replace(LANDHOLDING_OPTION_PREFIX, "") : uuid;
    const objectType = isLandholding ? "Landholding" : "Project";
    const name = projectsSelectData.find((x) => x.key === uuid)?.value as string;

    setFormValues((prev) => ({
      ...prev,
      associations: [
        ...prev.associations,
        {
          objectUuid,
          objectType,
          name,
        },
      ],
    }));
  };

  const removeAssociation = (association: DealAssociation): void => {
    setFormValues((prev) => ({
      ...prev,
      associations: prev.associations.filter(
        (value) => !(value.objectUuid === association.objectUuid && value.objectType === association.objectType)
      ),
    }));
  };

  const openAddEditCompanyModal = useCallback(() => {
    setShowAddEditCompanyModal(true);
  }, []);

  const closeAddEditCompanyModal = useCallback(() => {
    setShowAddEditCompanyModal(false);
  }, []);

  const openProjectCreatePanel = useCallback(() => {
    setShowProjectCreatePanel(true);
  }, []);

  const closeProjectCreatePanel = useCallback(() => {
    setShowProjectCreatePanel(false);
  }, []);

  const saveCompanyDraft = useCallback(
    (draft: CompanyDraft) => {
      setFormValues((prev) => ({
        ...prev,
        companyDraft: draft,
      }));
    },
    [setFormValues]
  );

  const saveProjects = useCallback(
    (projects: ProjectDraft[], landholding: LandholdingDraft, isLandholding: boolean) => {
      setFormValues((prev) => ({
        ...prev,
        projectDrafts: [
          ...prev.projectDrafts,
          ...projects.map((p) => ({ ...p, landholdingName: isLandholding ? landholding.name : undefined })),
        ],
        landholdingDrafts: isLandholding ? [...prev.landholdingDrafts, landholding] : prev.landholdingDrafts,
      }));
    },
    [setFormValues]
  );

  const clearCompanyDraft = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      companyDraft: null,
    }));
  }, [setFormValues]);

  const removeLandholdingDraft = useCallback(
    (draftKey: string) => {
      setFormValues((prev) => {
        const landholding = prev.landholdingDrafts.find((draft) => draft.key === draftKey);
        return {
          ...prev,
          landholdingDrafts: prev.landholdingDrafts.filter((draft) => draft.key !== draftKey),
          projectDrafts: landholding
            ? prev.projectDrafts.filter((draft) => !landholding.projectKeys.includes(draft.key))
            : prev.projectDrafts,
        };
      });
    },
    [setFormValues]
  );

  const removeProjectDraft = useCallback(
    async (draftKey: string): Promise<void> => {
      const projectDraft = formValues.projectDrafts.find((draft) => draft.key === draftKey);
      const associationKey = projectDraft?.projectUuid ?? draftKey;

      if (projectDraft?.projectUuid) {
        const deleteProjectResponse = await deleteProject({ projectUuid: projectDraft.projectUuid });

        if (deleteProjectResponse.status === Status.Error) {
          Toast.error({
            message: deleteProjectResponse.errors?.[0]?.message ?? "Failed to delete project.",
          });
          return;
        }
      }

      setFormValues((prev) => ({
        ...prev,
        projectDrafts: prev.projectDrafts.filter((draft) => draft.key !== draftKey),
        landholdingDrafts: prev.landholdingDrafts.map((draft) => ({
          ...draft,
          projectKeys: draft.projectKeys.filter((x) => x !== draftKey),
        })),
        structureInvestments: prev.structureInvestments.map((investment) => ({
          ...investment,
          associations: investment.associations.filter((value) => value !== associationKey),
        })),
      }));
    },
    [formValues.projectDrafts, setFormValues]
  );

  return {
    errors,
    investingIntoSelectData,
    companySelectData,
    projectsSelectData: filteredProjectsSelectData,
    selectedInvestedInto,
    associations,
    companyDraft,
    projectDrafts,
    landholdingDrafts,
    showAddEditCompanyModal,
    showProjectCreatePanel,
    openAddEditCompanyModal,
    closeAddEditCompanyModal,
    openProjectCreatePanel,
    closeProjectCreatePanel,
    saveCompanyDraft,
    saveProjects,
    clearCompanyDraft,
    removeLandholdingDraft,
    removeProjectDraft,
    onInvestedIntoSelect,
    onCompanySelect,
    onProjectSelect,
    onCompanyTypeSearch,
    onProjectTypeSearch,
    removeAssociation,
  };
};
